import { useEffect, useRef, useState } from 'react';

interface UseImageOptimizationProps {
  eagerLoading?: boolean;
  imageUrl?: string;
  preloadThreshold?: number;
}

/**
 * Optimizes image loading for performance.
 * - Provides a ref (`containerRef`) to attach to the image container, and a boolean (`isInView`) indicating if the image should be loaded.
 * - If `eagerLoading` is true, the image is loaded immediately and preloaded using a <link rel="preload"> tag.
 * - If `eagerLoading` is false, it uses the Intersection Observer API to detect when the image container is near the viewport (with a customizable `preloadThreshold`), and only then triggers the image to load.
 * - This approach helps improve page load performance by deferring non-critical images until they are needed, while still allowing for eager loading of above-the-fold or critical images.
 */

export const useImageOptimization = ({
  eagerLoading = false,
  imageUrl,
  preloadThreshold = 50
}: UseImageOptimizationProps) => {
  const [isInView, setIsInView] = useState(eagerLoading);
  const containerRef = useRef<HTMLDivElement>(null);

  // Intersection Observer for better lazy loading control
  useEffect(() => {
    if (eagerLoading) {
      setIsInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: `${preloadThreshold}px 0px`, // Start loading before the image comes into view
        threshold: 0.1
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [eagerLoading, preloadThreshold]);

  // Preload critical images
  useEffect(() => {
    if (eagerLoading && imageUrl) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = imageUrl;
      link.fetchPriority = 'high';
      document.head.appendChild(link);

      return () => {
        if (document.head.contains(link)) {
          document.head.removeChild(link);
        }
      };
    }
    return undefined;
  }, [eagerLoading, imageUrl]);

  return {
    isInView,
    containerRef
  };
};
